import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  ROUTE_PATHS,
  formatCurrency,
  formatPct,
  groupVociByCategoria,
  calcScostamento,
  type Analisi,
  type VoceSpesa,
  type AnalisiKPI,
  type StatoPatrimoniale,
  type AnalisiCondomino,
  type Raccomandazione,
  type Condominio,
} from '@/lib/index';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

/* ─────────────────────────────────────────────
   COLORI REPORT (palette dal documento di ref.)
───────────────────────────────────────────── */
const COLORS_BAR = ['#1B4F8A', '#2471A3', '#2980B9', '#3498DB', '#5DADE2'];
const COLORS_PIE = ['#1B4F8A', '#2471A3', '#E67E22', '#27AE60', '#C0392B', '#8E44AD', '#16A085', '#D35400'];
const COLOR_PREVENTIVO = '#1B4F8A';
const COLOR_CONSUNTIVO = '#E67E22';
const COLOR_POSITIVE = '#C0392B'; // sforamento
const COLOR_NEGATIVE = '#27AE60'; // risparmio
const TEAL = '#00B5AD';

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (v: number) =>
  new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const fmtSign = (v: number) => (v > 0 ? '+' : '') + fmt(v);

const fmtPct = (v: number | null) => {
  if (v === null) return 'n.p.';
  return (v > 0 ? '+' : '') + v.toFixed(1) + '%';
};

const shortLabel = (label: string, max = 14) =>
  label.length > max ? label.substring(0, max - 1) + '…' : label;

/* ─────────────────────────────────────────────
   GRAFICI INTERNI (specifici per stampa)
───────────────────────────────────────────── */
function GraficoBarreConfronto({
  data,
}: {
  data: { nome: string; preventivo: number; consuntivo: number }[];
}) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="nome"
          tick={{ fontSize: 10, fill: '#374151' }}
          tickFormatter={(v) => shortLabel(v, 16)}
          angle={-20}
          textAnchor="end"
          height={55}
        />
        <YAxis
          tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
          tick={{ fontSize: 10, fill: '#374151' }}
        />
        <Tooltip
          formatter={(value: number, name: string) => [formatCurrency(value), name === 'preventivo' ? 'Preventivo' : 'Consuntivo']}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
          formatter={(v) => (v === 'preventivo' ? 'Preventivo' : 'Consuntivo')}
        />
        <Bar dataKey="preventivo" fill={COLOR_PREVENTIVO} name="preventivo" radius={[3, 3, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={COLOR_PREVENTIVO} />
          ))}
        </Bar>
        <Bar dataKey="consuntivo" fill={COLOR_CONSUNTIVO} name="consuntivo" radius={[3, 3, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={COLOR_CONSUNTIVO} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function GraficoScostamenti({ data }: { data: { nome: string; pct: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 15, right: 20, left: 10, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="nome"
          tick={{ fontSize: 10, fill: '#374151' }}
          tickFormatter={(v) => shortLabel(v, 14)}
          angle={-20}
          textAnchor="end"
          height={55}
        />
        <YAxis
          tickFormatter={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`}
          tick={{ fontSize: 10, fill: '#374151' }}
        />
        <ReferenceLine y={0} stroke="#6b7280" strokeWidth={1.5} />
        <Tooltip formatter={(v: number) => [`${v > 0 ? '+' : ''}${v.toFixed(1)}%`, 'Scostamento']} />
        <Bar dataKey="pct" name="Scostamento %" radius={[3, 3, 0, 0]}>
          {data.map((entry, i) => (
            <Cell key={i} fill={entry.pct > 0 ? COLOR_POSITIVE : COLOR_NEGATIVE} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function GraficoTorta({ data }: { data: { nome: string; valore: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey="valore"
          nameKey="nome"
          cx="50%"
          cy="50%"
          outerRadius={85}
          label={({ nome, percent }) => `${shortLabel(nome, 12)} ${(percent * 100).toFixed(0)}%`}
          labelLine={true}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(v: number) => [formatCurrency(v), '']} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/* ─────────────────────────────────────────────
   COMPONENTI SEZIONE
───────────────────────────────────────────── */
function PageBreak() {
  return <div className="page-break" />;
}

function SectionTitle({ children, color = TEAL }: { children: React.ReactNode; color?: string }) {
  return (
    <div
      className="flex items-center gap-3 mb-4 pb-2 border-b-2"
      style={{ borderColor: color }}
    >
      <h2 className="text-base font-bold uppercase tracking-wider" style={{ color }}>
        {children}
      </h2>
    </div>
  );
}

function KPIBox({
  label,
  value,
  sub,
  color = '#1e293b',
  textColor = '#fff',
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  textColor?: string;
}) {
  return (
    <div
      className="rounded-lg p-4 flex flex-col gap-1"
      style={{ backgroundColor: color, color: textColor }}
    >
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-xl font-bold font-mono">{value}</p>
      {sub && <p className="text-xs opacity-70">{sub}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PAGINA PRINCIPALE
───────────────────────────────────────────── */
export default function ReportStampa() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reportRef = useRef<HTMLDivElement>(null);

  const [analisi, setAnalisi] = useState<Analisi | null>(null);
  const [condominio, setCondominio] = useState<Condominio | null>(null);
  const [voci, setVoci] = useState<VoceSpesa[]>([]);
  const [kpiList, setKpiList] = useState<AnalisiKPI[]>([]);
  const [statoPatrimoniale, setStatoPatrimoniale] = useState<StatoPatrimoniale[]>([]);
  const [condomini, setCondomini] = useState<AnalisiCondomino[]>([]);
  const [raccomandazioni, setRaccomandazioni] = useState<Raccomandazione[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const fetchAll = async () => {
      setLoading(true);
      const [
        { data: analisiData },
        { data: vociData },
        { data: kpiData },
        { data: spData },
        { data: condData },
        { data: raccData },
      ] = await Promise.all([
        supabase.from('analisi').select('*, condomini(nome, indirizzo, cf, iban, amministratore, assicurazione_compagnia, assicurazione_polizza, ascensore_ditta, ascensore_matricola, impianti_termici_ditta, verifica_elevatori, verifica_acqua, note)').eq('id', id).single(),
        supabase.from('voci_spesa').select('*').eq('analisi_id', id).order('macro_categoria').order('categoria'),
        supabase.from('analisi_kpi').select('*').eq('analisi_id', id).order('ordine'),
        supabase.from('analisi_stato_patrimoniale').select('*').eq('analisi_id', id).order('ordine'),
        supabase.from('analisi_condomini').select('*').eq('analisi_id', id).order('nominativo'),
        supabase.from('analisi_raccomandazioni').select('*').eq('analisi_id', id).order('ordine'),
      ]);

      if (analisiData) {
        const a = analisiData as Analisi & { condomini?: Condominio };
        setAnalisi(a);
        if (a.condomini) setCondominio(a.condomini as Condominio);
      }
      if (vociData) setVoci(vociData as VoceSpesa[]);
      if (kpiData) setKpiList(kpiData as AnalisiKPI[]);
      if (spData) setStatoPatrimoniale(spData as StatoPatrimoniale[]);
      if (condData) setCondomini(condData as AnalisiCondomino[]);
      if (raccData) setRaccomandazioni(raccData as Raccomandazione[]);
      setLoading(false);
    };
    fetchAll();
  }, [id]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Preparazione report...</p>
        </div>
      </div>
    );
  }

  if (!analisi) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-600">Analisi non trovata.</p>
      </div>
    );
  }

  const macroCategorie = groupVociByCategoria(voci);
  const hasPreventivo = (analisi.totale_preventivo ?? 0) > 0;
  const scostTot = calcScostamento(analisi.totale_uscite, analisi.totale_preventivo ?? 0);

  const barData = macroCategorie.map((m) => ({
    nome: m.nome,
    preventivo: m.totale_preventivo,
    consuntivo: m.totale_consuntivo,
  }));

  const scostData = macroCategorie
    .filter((m) => m.totale_preventivo > 0 && m.scostamento_pct !== null)
    .map((m) => ({ nome: m.nome, pct: m.scostamento_pct as number }));

  const pieData = macroCategorie
    .filter((m) => m.totale_consuntivo > 0)
    .map((m) => ({ nome: m.nome, valore: m.totale_consuntivo }));

  const attivita = statoPatrimoniale.filter((v) => v.tipo_sp === 'attivo' || v.tipo_sp === 'attivita');
  const passivita = statoPatrimoniale.filter((v) => v.tipo_sp === 'passivo' || v.tipo_sp === 'passivita');

  // Morosità: condomini con saldo finale POSITIVO = debitori (saldo_finale > 0 significa che devono ancora pagare)
  // Ordina dal più moroso (importo più alto) al meno moroso
  const morosi = condomini
    .filter((c) => (c.saldo_finale ?? 0) > 10)
    .sort((a, b) => (b.saldo_finale ?? 0) - (a.saldo_finale ?? 0));

  // Fallback: se nessuno ha saldo_finale > 0, prova con saldo negativo (vecchia convenzione)
  const morosiAlt = condomini
    .filter((c) => (c.saldo_finale ?? 0) < -10)
    .sort((a, b) => (a.saldo_finale ?? 0) - (b.saldo_finale ?? 0));

  const morositaList = morosi.length > 0 ? morosi : morosiAlt;

  const totaleMorosi = morositaList.reduce((s, c) => s + Math.abs(c.saldo_finale ?? 0), 0);
  // Totale crediti = somma di tutte le quote dovute da tutti i condomini
  const totaleCrediti = condomini.reduce((s, c) => s + (c.tot_consuntivo ?? c.totale_consuntivo ?? 0), 0);
  const pctMorosita = totaleCrediti > 0 ? (totaleMorosi / totaleCrediti * 100) : 0;

  const oggi = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  const nomeCondo = condominio?.nome ?? analisi.nome_condominio ?? 'Condominio';
  const nota = analisi.nota_sintetica ?? analisi.nota_sintesi ?? '';

  return (
    <>
      {/* ─── TOOLBAR (solo schermo) ─── */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white flex items-center justify-between px-6 py-3 shadow-lg">
        <button
          onClick={() => navigate(`/analisi/${id}`)}
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna all'analisi
        </button>
        <span className="text-sm font-medium text-teal-400">Anteprima Report di Stampa</span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4 mr-1" />
            Stampa / PDF
          </Button>
        </div>
      </div>

      {/* ─── REPORT A4 ─── */}
      <div ref={reportRef} className="report-wrapper">

        {/* ══════════════════════════════════
            PAGINA 1 – COPERTINA
        ══════════════════════════════════ */}
        <div className="report-page cover-page">
          {/* Header copertina */}
          <div className="cover-header">
            <div className="flex items-center gap-4">
              <img src="/images/Logo Egim.jpg" alt="EGIM SM" className="h-16 w-auto object-contain" />
              <div>
                <p className="text-white text-xl font-bold tracking-wider">EGIM SM</p>
                <p className="text-teal-200 text-sm">Gestioni Condominiali</p>
              </div>
            </div>
            <div className="text-right text-white/80 text-xs">
              <p>Analisi Bilancio Condominiale</p>
              <p>Confronto Preventivo vs Consuntivo</p>
            </div>
          </div>

          {/* Corpo copertina */}
          <div className="cover-body">
            <div className="cover-title-block">
              <p className="text-teal-300 text-sm font-medium uppercase tracking-widest mb-2">
                Report Ufficiale
              </p>
              <h1 className="text-4xl font-bold text-white mb-1">
                Analisi Bilancio Condominiale
              </h1>
              <p className="text-2xl text-teal-300 font-light mb-8">
                Confronto Preventivo vs Consuntivo {analisi.anno}
              </p>

              <div className="cover-info-grid">
                <InfoRow label="Condominio" value={nomeCondo} />
                {condominio?.indirizzo && <InfoRow label="Indirizzo" value={condominio.indirizzo} />}
                {condominio?.cf && <InfoRow label="Codice Fiscale" value={condominio.cf} />}
                {condominio?.iban && <InfoRow label="IBAN" value={condominio.iban} />}
                {analisi.tipo_gestione && <InfoRow label="Gestione" value={analisi.tipo_gestione} />}
                {condominio?.amministratore && <InfoRow label="Amministratore" value={condominio.amministratore} />}
                {condominio?.assicurazione_compagnia && (
                  <InfoRow
                    label="Assicurazione"
                    value={`${condominio.assicurazione_compagnia}${condominio.assicurazione_polizza ? ` – Polizza n° ${condominio.assicurazione_polizza}` : ''}`}
                  />
                )}
                {condominio?.ascensore_ditta && (
                  <InfoRow
                    label="Manut. Ascensore"
                    value={`${condominio.ascensore_ditta}${condominio.ascensore_matricola ? ` – Mat. ${condominio.ascensore_matricola}` : ''}`}
                  />
                )}
                {condominio?.impianti_termici_ditta && (
                  <InfoRow label="Impianti Termici" value={condominio.impianti_termici_ditta} />
                )}
                <InfoRow label="Data elaborazione" value={oggi} />
              </div>
            </div>
          </div>

          {/* Footer copertina */}
          <div className="cover-footer">
            <p>Elaborato da EGIM SM · Gestioni Condominiali Professionali · © 2026</p>
          </div>
        </div>

        {/* ══════════════════════════════════
            PAGINA 2 – RIEPILOGO ESECUTIVO
        ══════════════════════════════════ */}
        <div className="report-page">
          <ReportHeader title="Riepilogo Esecutivo" anno={analisi.anno} condo={nomeCondo} />

          {/* KPI principali */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <KPIBox
              label="Consuntivo Totale"
              value={formatCurrency(analisi.totale_uscite)}
              color="#1B4F8A"
            />
            <KPIBox
              label="Preventivo Totale"
              value={hasPreventivo ? formatCurrency(analisi.totale_preventivo!) : 'N/D'}
              color="#2471A3"
            />
            <KPIBox
              label="Scostamento"
              value={hasPreventivo ? fmtSign(scostTot.euro) : 'N/D'}
              sub={hasPreventivo ? fmtPct(scostTot.percentuale) : undefined}
              color={scostTot.euro > 0 ? '#C0392B' : scostTot.euro < 0 ? '#27AE60' : '#374151'}
            />
          </div>

          {/* KPI da DB */}
          {kpiList.length > 0 && (
            <div className="mb-6">
              <SectionTitle>Indicatori di Gestione</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                {kpiList.slice(0, 6).map((kpi) => (
                  <div
                    key={kpi.id}
                    className="rounded-lg border p-3 flex flex-col gap-1"
                    style={{
                      borderColor:
                        kpi.stato === 'critico'
                          ? '#C0392B'
                          : kpi.stato === 'attenzione'
                          ? '#F59E0B'
                          : '#27AE60',
                      borderLeftWidth: 4,
                    }}
                  >
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{kpi.label ?? kpi.titolo}</p>
                    <p className="text-lg font-bold font-mono text-gray-800">{kpi.valore}</p>
                    {kpi.dettaglio && <p className="text-xs text-gray-500">{kpi.dettaglio}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabella riepilogo per macro-categoria */}
          <SectionTitle>Riepilogo per Categoria</SectionTitle>
          <table className="report-table">
            <thead>
              <tr>
                <th className="text-left">Categoria</th>
                {hasPreventivo && <th className="text-right">Preventivo (€)</th>}
                <th className="text-right">Consuntivo (€)</th>
                {hasPreventivo && <th className="text-right">Scost. (€)</th>}
                {hasPreventivo && <th className="text-right">Scost. (%)</th>}
              </tr>
            </thead>
            <tbody>
              {macroCategorie.map((m) => (
                <tr key={m.nome}>
                  <td className="font-medium">{m.nome}</td>
                  {hasPreventivo && <td className="text-right font-mono text-gray-600">{fmt(m.totale_preventivo)}</td>}
                  <td className="text-right font-mono font-semibold">{fmt(m.totale_consuntivo)}</td>
                  {hasPreventivo && (
                    <td
                      className="text-right font-mono font-semibold"
                      style={{ color: m.scostamento_euro > 0 ? COLOR_POSITIVE : m.scostamento_euro < 0 ? COLOR_NEGATIVE : undefined }}
                    >
                      {fmtSign(m.scostamento_euro)}
                    </td>
                  )}
                  {hasPreventivo && (
                    <td
                      className="text-right font-mono font-semibold"
                      style={{
                        color:
                          m.scostamento_pct !== null && Math.abs(m.scostamento_pct) > 10
                            ? m.scostamento_euro > 0
                              ? COLOR_POSITIVE
                              : COLOR_NEGATIVE
                            : '#374151',
                      }}
                    >
                      {fmtPct(m.scostamento_pct)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="font-bold">TOTALE COMPLESSIVO</td>
                {hasPreventivo && <td className="text-right font-mono font-bold">{fmt(analisi.totale_preventivo!)}</td>}
                <td className="text-right font-mono font-bold">{fmt(analisi.totale_uscite)}</td>
                {hasPreventivo && (
                  <td
                    className="text-right font-mono font-bold"
                    style={{ color: scostTot.euro > 0 ? COLOR_POSITIVE : scostTot.euro < 0 ? COLOR_NEGATIVE : undefined }}
                  >
                    {fmtSign(scostTot.euro)}
                  </td>
                )}
                {hasPreventivo && (
                  <td
                    className="text-right font-mono font-bold"
                    style={{ color: scostTot.euro > 0 ? COLOR_POSITIVE : scostTot.euro < 0 ? COLOR_NEGATIVE : undefined }}
                  >
                    {fmtPct(scostTot.percentuale)}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>

          <ReportFooter page={2} />
        </div>

        {/* ══════════════════════════════════
            PAGINA 3 – GRAFICI
        ══════════════════════════════════ */}
        {voci.length > 0 && (
          <div className="report-page">
            <ReportHeader title="Analisi Grafica" anno={analisi.anno} condo={nomeCondo} />

            {hasPreventivo && barData.length > 0 && (
              <div className="mb-6">
                <SectionTitle>Confronto Macro-Categorie</SectionTitle>
                <GraficoBarreConfronto data={barData} />
              </div>
            )}

            {hasPreventivo && scostData.length > 0 && (
              <div className="mb-6">
                <SectionTitle>Scostamenti Percentuali per Categoria</SectionTitle>
                <p className="text-xs text-gray-500 mb-2">
                  Rosso = sforamento rispetto al preventivo · Verde = risparmio
                </p>
                <GraficoScostamenti data={scostData} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              {pieData.length > 0 && (
                <div>
                  <SectionTitle>Composizione Consuntivo</SectionTitle>
                  <GraficoTorta data={pieData} />
                </div>
              )}
              {hasPreventivo && (
                <div>
                  <SectionTitle>Composizione Preventivo</SectionTitle>
                  <GraficoTorta
                    data={macroCategorie
                      .filter((m) => m.totale_preventivo > 0)
                      .map((m) => ({ nome: m.nome, valore: m.totale_preventivo }))}
                  />
                </div>
              )}
            </div>

            <ReportFooter page={3} />
          </div>
        )}

        {/* ══════════════════════════════════
            PAGINA 4 – DETTAGLIO VOCI PER CATEGORIA (compatto, tutte in 1 pagina)
        ══════════════════════════════════ */}
        {voci.length > 0 && (
          <div className="report-page">
            <ReportHeader title="Dettaglio Voci di Spesa" anno={analisi.anno} condo={nomeCondo} />

            {macroCategorie.map((macro, idx) => (
              <div key={macro.nome} className="mb-5">
                {/* Titolo macro-categoria compatto */}
                <div className="macro-cat-header mb-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-xs uppercase tracking-wider" style={{ color: TEAL }}>
                      {String(idx + 1).padStart(2, '0')} – {macro.nome}
                    </h3>
                    <div className="text-xs text-gray-600">
                      {hasPreventivo && (
                        <span>Prev. {formatCurrency(macro.totale_preventivo)} → </span>
                      )}
                      <span className="font-semibold">Cons. {formatCurrency(macro.totale_consuntivo)}</span>
                      {hasPreventivo && macro.scostamento_pct !== null && (
                        <span
                          className="ml-2 font-bold"
                          style={{ color: macro.scostamento_euro > 0 ? COLOR_POSITIVE : COLOR_NEGATIVE }}
                        >
                          ({fmtPct(macro.scostamento_pct)})
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <table className="report-table-compact">
                  <thead>
                    <tr>
                      <th className="text-left" style={{ width: 60 }}>Cod.</th>
                      <th className="text-left">Descrizione</th>
                      {hasPreventivo && <th className="text-right" style={{ width: 90 }}>Prev. (€)</th>}
                      <th className="text-right" style={{ width: 90 }}>Cons. (€)</th>
                      {hasPreventivo && <th className="text-right" style={{ width: 80 }}>Sc. €</th>}
                      {hasPreventivo && <th className="text-right" style={{ width: 65 }}>Sc. %</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {macro.voci.map((voce) => {
                      const scostE = voce.scostamento_euro ?? (voce.importo_consuntivo - voce.importo_preventivo);
                      const scostP = voce.scostamento_pct ?? voce.scostamento_percentuale ?? null;
                      const nonPrev = voce.is_non_preventivata;
                      return (
                        <tr key={voce.id}>
                          <td className="font-mono" style={{ color: '#9ca3af', fontSize: 9 }}>{voce.codice ?? '–'}</td>
                          <td className={nonPrev ? 'italic' : ''} style={{ color: nonPrev ? '#6b7280' : '#374151' }}>
                            {voce.descrizione}
                            {nonPrev && <span style={{ fontSize: 9, color: '#d97706', marginLeft: 4 }}>[n.p.]</span>}
                          </td>
                          {hasPreventivo && (
                            <td className="text-right font-mono" style={{ color: '#6b7280' }}>
                              {voce.importo_preventivo > 0 ? fmt(voce.importo_preventivo) : '–'}
                            </td>
                          )}
                          <td className="text-right font-mono" style={{ fontWeight: 500 }}>
                            {voce.importo_consuntivo > 0 ? fmt(voce.importo_consuntivo) : '–'}
                          </td>
                          {hasPreventivo && (
                            <td
                              className="text-right font-mono"
                              style={{ color: nonPrev ? '#92400E' : scostE > 0 ? COLOR_POSITIVE : scostE < 0 ? COLOR_NEGATIVE : undefined }}
                            >
                              {nonPrev ? 'n.p.' : scostE !== 0 ? fmtSign(scostE) : '–'}
                            </td>
                          )}
                          {hasPreventivo && (
                            <td
                              className="text-right font-mono"
                              style={{
                                fontWeight: scostP !== null && Math.abs(scostP) > 10 ? 700 : 400,
                                color: nonPrev ? '#92400E' : scostP !== null && Math.abs(scostP) > 10 ? (scostE > 0 ? COLOR_POSITIVE : COLOR_NEGATIVE) : '#6b7280',
                              }}
                            >
                              {nonPrev ? 'n.p.' : fmtPct(scostP)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ fontWeight: 700 }}>Totale {macro.nome}</td>
                      {hasPreventivo && <td className="text-right font-mono" style={{ fontWeight: 700 }}>{fmt(macro.totale_preventivo)}</td>}
                      <td className="text-right font-mono" style={{ fontWeight: 700 }}>{fmt(macro.totale_consuntivo)}</td>
                      {hasPreventivo && (
                        <td
                          className="text-right font-mono"
                          style={{ fontWeight: 700, color: macro.scostamento_euro > 0 ? COLOR_POSITIVE : macro.scostamento_euro < 0 ? COLOR_NEGATIVE : undefined }}
                        >
                          {fmtSign(macro.scostamento_euro)}
                        </td>
                      )}
                      {hasPreventivo && (
                        <td
                          className="text-right font-mono"
                          style={{ fontWeight: 700, color: macro.scostamento_euro > 0 ? COLOR_POSITIVE : macro.scostamento_euro < 0 ? COLOR_NEGATIVE : undefined }}
                        >
                          {fmtPct(macro.scostamento_pct)}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}

            <ReportFooter page={4} />
          </div>
        )}

        {/* ══════════════════════════════════
            PAGINA – STATO PATRIMONIALE
        ══════════════════════════════════ */}
        {statoPatrimoniale.length > 0 && (
          <div className="report-page">
            <ReportHeader title="Stato Patrimoniale" anno={analisi.anno} condo={nomeCondo} />

            <div className="grid grid-cols-2 gap-6">
              {/* ATTIVITÀ */}
              <div>
                <SectionTitle color="#27AE60">Attività</SectionTitle>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th className="text-left">Voce</th>
                      <th className="text-right">Importo (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attivita.map((v) => (
                      <tr key={v.id}>
                        <td>{v.voce}</td>
                        <td className="text-right font-mono">{fmt(v.importo)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="font-bold">Totale Attività</td>
                      <td className="text-right font-mono font-bold" style={{ color: '#27AE60' }}>
                        {fmt(attivita.reduce((s, v) => s + v.importo, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* PASSIVITÀ */}
              <div>
                <SectionTitle color="#C0392B">Passività</SectionTitle>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th className="text-left">Voce</th>
                      <th className="text-right">Importo (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passivita.map((v) => (
                      <tr key={v.id}>
                        <td>{v.voce}</td>
                        <td className="text-right font-mono">{fmt(v.importo)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="font-bold">Totale Passività</td>
                      <td className="text-right font-mono font-bold" style={{ color: '#C0392B' }}>
                        {fmt(passivita.reduce((s, v) => s + v.importo, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <ReportFooter page={5} />
          </div>
        )}

        {/* ══════════════════════════════════
            PAGINA – ANALISI MOROSITÀ
        ══════════════════════════════════ */}
        {morositaList.length > 0 && (
          <div className="report-page">
            <ReportHeader title="Analisi Morosità" anno={analisi.anno} condo={nomeCondo} />

            {/* Riquadro riepilogo */}
            {/* Riquadro riepilogo con % sui crediti */}
            <div
              className="rounded-lg p-4 mb-5"
              style={{ backgroundColor: '#FEF2F2', border: '2px solid #FCA5A5' }}
            >
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <p className="text-xs uppercase font-semibold" style={{ color: '#991B1B' }}>Condomini Morosi</p>
                  <p className="text-2xl font-bold font-mono" style={{ color: '#B91C1C' }}>{morositaList.length}</p>
                  <p className="text-xs" style={{ color: '#991B1B' }}>su {condomini.length} totali</p>
                </div>
                <div style={{ borderLeft: '2px solid #FCA5A5', paddingLeft: 20 }}>
                  <p className="text-xs uppercase font-semibold" style={{ color: '#991B1B' }}>Rate Non Versate</p>
                  <p className="text-2xl font-bold font-mono" style={{ color: '#B91C1C' }}>{fmt(totaleMorosi)}</p>
                </div>
                <div style={{ borderLeft: '2px solid #FCA5A5', paddingLeft: 20 }}>
                  <p className="text-xs uppercase font-semibold" style={{ color: '#991B1B' }}>Totale Crediti</p>
                  <p className="text-lg font-bold font-mono" style={{ color: '#374151' }}>{fmt(totaleCrediti > 0 ? totaleCrediti : analisi.totale_uscite)}</p>
                </div>
                <div style={{ borderLeft: '2px solid #FCA5A5', paddingLeft: 20 }}>
                  <p className="text-xs uppercase font-semibold" style={{ color: '#991B1B' }}>% Rate Non Versate su Crediti</p>
                  <p className="text-2xl font-bold font-mono" style={{ color: pctMorosita > 20 ? '#B91C1C' : pctMorosita > 5 ? '#D97706' : '#374151' }}>
                    {pctMorosita.toFixed(2)}%
                  </p>
                  <p className="text-xs" style={{ color: '#991B1B' }}>dei crediti totali non riscossi</p>
                </div>
              </div>
              {pctMorosita > 10 && (
                <p className="text-xs font-bold mt-3" style={{ color: '#B91C1C' }}>
                  ⚠ La percentuale di morosità supera il 10% dei crediti totali — si raccomanda azione immediata di recupero crediti.
                </p>
              )}
            </div>

            <SectionTitle color="#B91C1C">Classifica Morosità (dal più moroso al meno moroso)</SectionTitle>
            <table className="report-table">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 40 }}>#</th>
                  <th className="text-left">Nominativo</th>
                  <th className="text-right">Quota Dovuta (€)</th>
                  <th className="text-right">Versamenti (€)</th>
                  <th className="text-right" style={{ backgroundColor: '#7F1D1D', color: 'white' }}>Rate Non Versate (€)</th>
                  <th className="text-right" style={{ backgroundColor: '#7F1D1D', color: 'white' }}>% su Crediti</th>
                </tr>
              </thead>
              <tbody>
                {morositaList.map((c, i) => {
                  const totCons = c.tot_consuntivo ?? c.totale_consuntivo ?? 0;
                  const vers = c.vers_gest_corrente ?? c.versamenti ?? 0;
                  const saldoFin = Math.abs(c.saldo_finale ?? 0);
                  const creditiBase = totaleCrediti > 0 ? totaleCrediti : analisi.totale_uscite;
                  const pctSingolo = creditiBase > 0 ? (saldoFin / creditiBase * 100) : 0;
                  return (
                    <tr key={c.id} style={{ backgroundColor: i === 0 ? '#FEF2F2' : undefined }}>
                      <td className="text-center font-bold font-mono" style={{ color: i < 3 ? '#B91C1C' : '#374151' }}>
                        {i + 1}
                      </td>
                      <td className="font-semibold">{c.nominativo}</td>
                      <td className="text-right font-mono">{totCons > 0 ? fmt(totCons) : '—'}</td>
                      <td className="text-right font-mono">{vers > 0 ? fmt(vers) : '—'}</td>
                      <td
                        className="text-right font-mono font-bold"
                        style={{ color: '#B91C1C', backgroundColor: '#FEF2F2' }}
                      >
                        {fmt(saldoFin)}
                      </td>
                      <td
                        className="text-right font-mono font-semibold"
                        style={{ color: '#B91C1C' }}
                      >
                        {pctSingolo.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="font-bold">Totale rate non versate</td>
                  <td
                    className="text-right font-mono font-bold"
                    style={{ color: '#B91C1C' }}
                  >
                    {fmt(totaleMorosi)}
                  </td>
                  <td
                    className="text-right font-mono font-bold"
                    style={{ color: '#B91C1C' }}
                  >
                    {pctMorosita.toFixed(2)}%
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}>
              <p className="text-xs font-semibold" style={{ color: '#92400E', marginBottom: 4 }}>⚠ Note sulla morosità</p>
              <p className="text-xs" style={{ color: '#78350F', lineHeight: 1.6 }}>
                I condomini elencati risultano inadempienti nel versamento delle quote condominiali.
                Le rate non versate rappresentano il <strong>{pctMorosita.toFixed(2)}%</strong> del totale
                dei crediti condominiali ({fmt(totaleCrediti > 0 ? totaleCrediti : analisi.totale_uscite)}).
                Si raccomanda l'invio di solleciti formali e, se necessario,
                l'avvio delle procedure di recupero crediti previste dal Codice Civile (art. 63 disp. att. c.c.).
              </p>
            </div>

            <ReportFooter page={5} />
          </div>
        )}

        {/* ══════════════════════════════════
            PAGINA – PROSPETTO DI RIPARTO
        ══════════════════════════════════ */}
        {condomini.length > 0 && (
          <div className="report-page landscape-page">
            <ReportHeader title="Prospetto di Riparto Consuntivo" anno={analisi.anno} condo={nomeCondo} />

            <table className="report-table riparto-table">
              <thead>
                <tr>
                  <th className="text-left">Nominativo</th>
                  <th className="text-right">Tot. Cons. (€)</th>
                  <th className="text-right">Saldo Prec. (€)</th>
                  <th className="text-right">Versamenti (€)</th>
                  <th className="text-right">Saldo Finale (€)</th>
                  <th className="text-center">Stato</th>
                </tr>
              </thead>
              <tbody>
                {condomini
                  .slice()
                  .sort((a, b) => a.nominativo.localeCompare(b.nominativo, 'it'))
                  .map((c) => {
                    const totCons = c.totale_consuntivo ?? c.tot_consuntivo ?? 0;
                    const saldoPrec = c.saldo_ordinario ?? c.saldo_prec ?? 0;
                    const vers = c.vers_gest_corrente ?? c.versamenti ?? 0;
                    const saldoFin = c.saldo_finale ?? 0;
                    return (
                      <tr key={c.id}>
                        <td className="font-medium">{c.nominativo}</td>
                        <td className="text-right font-mono">{fmt(totCons)}</td>
                        <td
                          className="text-right font-mono"
                          style={{ color: saldoPrec < 0 ? COLOR_POSITIVE : saldoPrec > 0 ? COLOR_NEGATIVE : '#374151' }}
                        >
                          {fmtSign(saldoPrec)}
                        </td>
                        <td className="text-right font-mono">{fmt(vers)}</td>
                        <td
                          className="text-right font-mono font-bold"
                          style={{ color: saldoFin < 0 ? COLOR_POSITIVE : saldoFin > 0 ? COLOR_NEGATIVE : '#374151' }}
                        >
                          {fmtSign(saldoFin)}
                        </td>
                        <td className="text-center">
                          <span
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{
                              backgroundColor: saldoFin < 0 ? '#FEE2E2' : '#D1FAE5',
                              color: saldoFin < 0 ? '#991B1B' : '#065F46',
                            }}
                          >
                            {saldoFin < 0 ? '⚠ Debitore' : '✓ Creditore'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
              <tfoot>
                <tr>
                  <td className="font-bold">Totale Generale</td>
                  <td className="text-right font-mono font-bold">
                    {fmt(condomini.reduce((s, c) => s + (c.totale_consuntivo ?? c.tot_consuntivo ?? 0), 0))}
                  </td>
                  <td className="text-right font-mono font-bold">
                    {fmtSign(condomini.reduce((s, c) => s + (c.saldo_ordinario ?? c.saldo_prec ?? 0), 0))}
                  </td>
                  <td className="text-right font-mono font-bold">
                    {fmt(condomini.reduce((s, c) => s + (c.vers_gest_corrente ?? c.versamenti ?? 0), 0))}
                  </td>
                  <td className="text-right font-mono font-bold">
                    {fmtSign(condomini.reduce((s, c) => s + (c.saldo_finale ?? 0), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>

            <ReportFooter page={6} />
          </div>
        )}

        {/* ══════════════════════════════════
            PAGINA – NOTE E OSSERVAZIONI
        ══════════════════════════════════ */}
        <div className="report-page">
          <ReportHeader title="Note e Osservazioni" anno={analisi.anno} condo={nomeCondo} />

          {/* Raccomandazioni */}
          {raccomandazioni.length > 0 && (
            <div className="mb-6">
              <SectionTitle>Raccomandazioni per il Prossimo Esercizio</SectionTitle>
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="text-left">Voce</th>
                    <th className="text-left">Raccomandazione</th>
                    <th className="text-center w-24">Priorità</th>
                  </tr>
                </thead>
                <tbody>
                  {raccomandazioni.map((r) => (
                    <tr key={r.id}>
                      <td className="font-medium text-sm">{r.titolo}</td>
                      <td className="text-sm text-gray-600">{r.testo}</td>
                      <td className="text-center">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor:
                              r.priorita === 'alta' ? '#FEE2E2' : r.priorita === 'media' ? '#FEF3C7' : '#D1FAE5',
                            color: r.priorita === 'alta' ? '#991B1B' : r.priorita === 'media' ? '#92400E' : '#065F46',
                          }}
                        >
                          {r.priorita?.toUpperCase() ?? 'BASSA'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Nota sintetica */}
          {nota && (
            <div>
              <SectionTitle>Nota Integrativa</SectionTitle>
              <div className="nota-text">
                <NoteRenderer nota={nota} />
              </div>
            </div>
          )}

          {/* Footer finale */}
          <div className="final-footer">
            <div className="flex items-center gap-3">
              <img src="/images/Logo Egim.jpg" alt="EGIM SM" className="h-8 w-auto object-contain opacity-70" />
              <span className="text-gray-500 text-xs">EGIM SM · Gestioni Condominiali</span>
            </div>
            <p className="text-gray-400 text-xs">
              Report generato il {oggi} · Condominio {nomeCondo} · Esercizio {analisi.anno}
            </p>
          </div>

          <ReportFooter page={8} />
        </div>

      </div>

      {/* ─── CSS STAMPA ─── */}
      <style>{`
        /* ── Layout pagine ── */
        .report-wrapper {
          background: #f3f4f6;
          padding: 24px;
          min-height: 100vh;
          margin-top: 56px;
          font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
        }

        /* ── Tabella compatta per dettaglio voci ── */
        .report-table-compact {
          width: 100%;
          border-collapse: collapse;
          font-size: 9.5px;
          margin-bottom: 2px;
        }
        .report-table-compact th {
          background: #334155;
          color: white;
          padding: 4px 6px;
          font-weight: 600;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .report-table-compact td {
          padding: 3px 6px;
          border-bottom: 1px solid #f1f5f9;
          color: #374151;
        }
        .report-table-compact tbody tr:nth-child(even) td {
          background: #f8fafc;
        }
        .report-table-compact tfoot td {
          background: #e9f0f5;
          border-top: 1.5px solid #94a3b8;
          padding: 4px 6px;
        }

        .report-page {
          background: white;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto 24px;
          padding: 16mm 16mm 14mm;
          box-shadow: 0 4px 24px rgba(0,0,0,0.12);
          position: relative;
          display: flex;
          flex-direction: column;
        }

        /* ── Copertina ── */
        .cover-page {
          background: linear-gradient(135deg, #0F1923 0%, #1A2535 60%, #0d3355 100%);
          padding: 0;
          overflow: hidden;
        }

        .cover-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 2px solid rgba(0,181,173,0.3);
        }

        .cover-body {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 40px;
        }

        .cover-title-block {
          width: 100%;
        }

        .cover-info-grid {
          display: grid;
          gap: 6px;
        }

        .cover-footer {
          text-align: center;
          padding: 14px;
          font-size: 10px;
          color: rgba(255,255,255,0.4);
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        /* ── Header pagina interna ── */
        .report-page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid #00B5AD;
        }

        /* ── Tabelle ── */
        .report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .report-table th {
          background: #1e293b;
          color: white;
          padding: 7px 8px;
          font-weight: 600;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .report-table td {
          padding: 6px 8px;
          border-bottom: 1px solid #f1f5f9;
          color: #374151;
        }

        .report-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .report-table tfoot td {
          background: #e2e8f0;
          font-weight: 700;
          border-top: 2px solid #94a3b8;
          padding: 8px;
        }

        .riparto-table {
          font-size: 10px;
        }

        .riparto-table th, .riparto-table td {
          padding: 5px 6px;
        }

        /* ── Macro categoria header ── */
        .macro-cat-header {
          background: #f0fdfa;
          border: 1px solid #99f6e4;
          border-left: 4px solid #00B5AD;
          padding: 8px 12px;
          border-radius: 6px;
        }

        /* ── Nota integrativa ── */
        .nota-text {
          font-size: 11px;
          line-height: 1.7;
          color: #374151;
        }

        /* ── Footer ── */
        .report-footer {
          margin-top: auto;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 9px;
          color: #9ca3af;
        }

        .final-footer {
          margin-top: auto;
          padding-top: 20px;
          border-top: 2px solid #00B5AD;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* ── Stampa ── */
        @media print {
          @page {
            size: A4;
            margin: 0;
          }

          @page landscape {
            size: A4 landscape;
            margin: 0;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          body {
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          .report-wrapper {
            background: white !important;
            padding: 0 !important;
            margin-top: 0 !important;
          }

          .report-page {
            box-shadow: none !important;
            page-break-after: always;
            break-after: page;
            margin: 0 !important;
            width: 100% !important;
          }

          .landscape-page {
            page: landscape;
          }

          .page-break {
            page-break-before: always;
          }
          .report-table-compact th,
          .report-table-compact td {
            font-size: 8.5px !important;
            padding: 2px 4px !important;
          }
        }
      `}</style>
    </>
  );
}

/* ─────────────────────────────────────────────
   COMPONENTI HELPER
───────────────────────────────────────────── */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-1 border-b border-white/10">
      <span className="text-teal-300 text-sm w-36 flex-shrink-0">{label}</span>
      <span className="text-white text-sm font-medium">{value}</span>
    </div>
  );
}

function ReportHeader({ title, anno, condo }: { title: string; anno: number; condo: string }) {
  return (
    <div className="report-page-header">
      <div>
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        <p className="text-xs text-gray-400">{condo}</p>
      </div>
      <div className="flex items-center gap-3">
        <img src="/images/Logo Egim.jpg" alt="EGIM" className="h-8 w-auto object-contain" />
        <div className="text-right">
          <p className="text-xs font-bold text-slate-700">EGIM SM</p>
          <p className="text-xs text-gray-400">Esercizio {anno}</p>
        </div>
      </div>
    </div>
  );
}

function ReportFooter({ page }: { page: number }) {
  return (
    <div className="report-footer">
      <span>EGIM SM · Gestioni Condominiali Professionali</span>
      <span>Pagina {page}</span>
    </div>
  );
}

function NoteRenderer({ nota }: { nota: string }) {
  // Pulizia profonda caratteri di escape (gestisce JSON di Claude mal deserializzato)
  let clean = nota || '';
  clean = clean
    .replace(/\\\\n/g, '\n')  // \\n doppio escape
    .replace(/\\n/g, '\n')    // \n singolo escape
    .replace(/\\\\t/g, ' ')
    .replace(/\\t/g, ' ')
    .replace(/\\\\'/g, "'")
    .replace(/\\'/g, "'")
    .replace(/\\\\"/g, '"')
    .replace(/\\"/g, '"')
    .trim();
  // Se mancano newline ma ci sono marker ## (stringa compatta)
  if (!clean.includes('\n') && clean.includes('##')) {
    clean = clean.replace(/##/g, '\n##').trim();
  }

  const lines = clean.split('\n');

  type Block =
    | { type: 'h1'; text: string }
    | { type: 'h2'; text: string }
    | { type: 'h3'; text: string }
    | { type: 'paragraph'; text: string }
    | { type: 'bullet'; text: string }
    | { type: 'spacer' };

  const blocks: Block[] = [];

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) {
      if (blocks.length > 0 && blocks[blocks.length - 1].type !== 'spacer') {
        blocks.push({ type: 'spacer' });
      }
      continue;
    }
    if (/^###\s+/.test(trimmed)) { blocks.push({ type: 'h3', text: trimmed.replace(/^###\s+/, '') }); continue; }
    if (/^##\s+/.test(trimmed))  { blocks.push({ type: 'h2', text: trimmed.replace(/^##\s+/, '') });  continue; }
    if (/^#\s+/.test(trimmed))   { blocks.push({ type: 'h1', text: trimmed.replace(/^#\s+/, '') });   continue; }
    if (/^[-*•]\s+/.test(trimmed)) { blocks.push({ type: 'bullet', text: trimmed.replace(/^[-*•]\s+/, '') }); continue; }
    if (
      trimmed === trimmed.toUpperCase() &&
      /^[A-ZÀÈÌÒÙÉ][A-ZÀÈÌÒÙÉ\s\d'.,()-]{3,}$/.test(trimmed) &&
      trimmed.length <= 80
    ) { blocks.push({ type: 'h2', text: trimmed.replace(/:$/, '') }); continue; }
    if (/^[A-ZÀÈÌÒÙÉ][\w\s'àèìòùéìù]{3,}:$/.test(trimmed) && trimmed.length <= 60) {
      blocks.push({ type: 'h3', text: trimmed.replace(/:$/, '') }); continue;
    }
    blocks.push({ type: 'paragraph', text: trimmed });
  }

  // Inline bold
  const renderInline = (text: string) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      /^\*\*[^*]+\*\*$/.test(part)
        ? <strong key={i} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>
        : <span key={i}>{part}</span>
    );

  // Raggruppa bullets
  type RGroup = { kind: 'bullets'; items: string[] } | { kind: 'block'; block: Block };
  const groups: RGroup[] = [];
  for (const block of blocks) {
    if (block.type === 'bullet') {
      const last = groups[groups.length - 1];
      if (last && last.kind === 'bullets') { last.items.push(block.text); }
      else { groups.push({ kind: 'bullets', items: [block.text] }); }
    } else {
      groups.push({ kind: 'block', block });
    }
  }

  return (
    <div>
      {groups.map((g, gi) => {
        if (g.kind === 'bullets') {
          return (
            <ul key={gi} style={{ paddingLeft: 0, listStyle: 'none', margin: '6px 0' }}>
              {g.items.map((item, ii) => (
                <li key={ii} style={{ display: 'flex', gap: 6, marginBottom: 3, fontSize: 11, lineHeight: 1.7, color: '#374151' }}>
                  <span style={{ color: '#00B5AD', flexShrink: 0 }}>▸</span>
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        const { block } = g;
        if (block.type === 'spacer') return <div key={gi} style={{ height: 8 }} />;
        if (block.type === 'h1') return (
          <div key={gi} style={{ borderBottom: '2px solid #00B5AD', marginBottom: 8, marginTop: 16, paddingBottom: 3 }}>
            <p style={{ fontWeight: 800, color: '#1B4F8A', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>{block.text}</p>
          </div>
        );
        if (block.type === 'h2') return (
          <div key={gi} style={{ borderLeft: '4px solid #00B5AD', paddingLeft: 8, marginTop: 12, marginBottom: 4, backgroundColor: '#f0fafa', borderRadius: '0 4px 4px 0' }}>
            <p style={{ fontWeight: 700, color: '#00B5AD', fontSize: 11, margin: '3px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{block.text}</p>
          </div>
        );
        if (block.type === 'h3') return (
          <div key={gi} style={{ marginTop: 8, marginBottom: 2 }}>
            <p style={{ fontWeight: 600, color: '#1B4F8A', fontSize: 11, margin: 0 }}>{block.text}</p>
          </div>
        );
        return (
          <p key={gi} style={{ fontSize: 11, lineHeight: 1.75, color: '#374151', margin: '3px 0' }}>
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}